import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DUNGEON_REWARDS } from '../data/entrance-types';

interface ZoneRewardPickerProps {
  zone: string;
  zoneRewards: Record<string, string[]>;
  onToggle: (zone: string, rewardId: string) => void;
  onClose: () => void;
  anchorRect: { top: number; left: number; bottom: number };
}

/** Find which zone (if any) a reward is currently assigned to */
function findRewardZone(rewardId: string, zoneRewards: Record<string, string[]>): string | null {
  for (const [z, rewards] of Object.entries(zoneRewards)) {
    if (rewards.includes(rewardId)) return z;
  }
  return null;
}

export function ZoneRewardPicker({ zone, zoneRewards, onToggle, onClose, anchorRect }: ZoneRewardPickerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const currentRewards = zoneRewards[zone] ?? [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-2"
      style={{ top: anchorRect.bottom + 4, left: anchorRect.left, zIndex: 9999 }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="text-[9px] text-gray-500 uppercase tracking-wider mb-1 px-1">Dungeon Rewards</div>
      <div className="grid grid-cols-3 gap-1">
        {DUNGEON_REWARDS.map((reward) => {
          const isHere = currentRewards.includes(reward.id);
          const otherZone = !isHere ? findRewardZone(reward.id, zoneRewards) : null;

          return (
            <button
              key={reward.id}
              className={`
                relative w-8 h-8 rounded cursor-pointer transition-all
                flex items-center justify-center
                ${isHere ? 'bg-zelda-gold/20 ring-1 ring-zelda-gold' : 'hover:bg-gray-800'}
                ${otherZone ? 'opacity-40' : ''}
              `}
              title={reward.label + (otherZone ? ` (in ${otherZone})` : '')}
              onClick={() => onToggle(zone, reward.id)}
            >
              <img
                src={reward.image}
                alt={reward.label}
                className="w-6 h-6"
                style={{ imageRendering: 'pixelated' }}
              />
              {otherZone && (
                <span className="absolute -bottom-0.5 text-[7px] text-gray-400 leading-none">
                  {otherZone}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}
